import { ReleaseStepEntityRequest } from '@/actions/entities';
import { BaseUpdateReleaseRequest, UpdateTranslationsApprovalRequest } from '@/actions/release';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { FeatureItem, ReleaseStep, RunwayModelsResponse } from '@/types';

export interface FetchReleaseStepResponse extends ReleaseStepEntityRequest {
    releaseStep: ReleaseStep;
}

export const FETCH_RELEASE_STEP_SUCCESS = 'FETCH_RELEASE_STEP_SUCCESS';
export const UPDATE_RELEASE_STEPS = 'UPDATE_RELEASE_STEPS';

export interface BaseUpdateReleaseStepRequest extends BaseUpdateReleaseRequest {
    stepRunId: string;
}

// feature readiness step

export interface BaseUpdateFeatureWorkItemRequest extends BaseUpdateReleaseStepRequest {
    frItemId: string;
}

export interface GetPendingAssociationTicketRequest extends BaseUpdateReleaseRequest {
    searchCriteria: string;
}

export interface GetPendingAssociationTicketResponse extends BaseUpdateReleaseRequest {
    data: Record<string, FeatureItem>;
    errorMessage?: string;
    success: boolean;
}

export interface IgnoreUnignoreFRItemRequest {
    items: IgnoreUnignoreFRItemRequestItem[];
}

export interface IgnoreUnignoreFRItemRequestItem {
    frItemId: string;
    isIgnore: boolean;
    isAutoIgnoreItem: boolean;
}

export interface AddFixRequestWorkItemData {
    id: string;
    pullRequestNumber?: number;
    ticketKey?: string;
    ticketUrl?: string;
}

export interface AddFixRequest {
    workItems: AddFixRequestWorkItemData[];
    details?: string;
    ticketKey?: string;
    ticketUrl?: string;
}

export interface UpdateFixRequest {
    fixId: string;
    details?: string;
}

export interface ApproveFixRequest {
    fixId: string;
    isAutoIgnoreItem: boolean;
}

export interface RejectFixRequest {
    fixId: string;
    isAutoIgnoreItem: boolean;
}

export interface MoveFixRequestToPendingApproval {
    fixId: string;
    isAutoIgnoreItem: boolean;
}

// translations step

interface BaseUpdateTranslationsRequest {
    branchName: string;
}

export type UploadTranslationsRequest = BaseUpdateTranslationsRequest;
export type ExportTranslationsRequest = BaseUpdateTranslationsRequest;
export type SyncTranslationsRequest = BaseUpdateTranslationsRequest;

export type UpdateReleaseStepRequestDataTypes<T extends ReleaseStepUpdatable> =
    // feature readiness
    T extends ReleaseStepUpdatable.AddFixRequest ? AddFixRequest
    : T extends ReleaseStepUpdatable.UpdateFixRequest ? UpdateFixRequest
    : T extends ReleaseStepUpdatable.ApproveFixRequest ? ApproveFixRequest
    : T extends ReleaseStepUpdatable.RejectFixRequest ? RejectFixRequest
    : T extends ReleaseStepUpdatable.MoveFixRequestToPendingApproval ? MoveFixRequestToPendingApproval
    : T extends ReleaseStepUpdatable.IgnoreFRItem | ReleaseStepUpdatable.UnignoreFRItem ? IgnoreUnignoreFRItemRequest
    : // translations
    T extends ReleaseStepUpdatable.UploadTranslationStrings ? UploadTranslationsRequest
    : T extends ReleaseStepUpdatable.ExportTranslationStrings ? ExportTranslationsRequest
    : T extends ReleaseStepUpdatable.SyncTranslationStrings ? SyncTranslationsRequest
    : T extends ReleaseStepUpdatable.TranslationsApproval ? UpdateTranslationsApprovalRequest
    : never;

export type UpdateReleaseStepResourceIds<T extends ReleaseStepUpdatable> =
    // feature readiness
    T extends (
        | ReleaseStepUpdatable.AddFixRequest
        | ReleaseStepUpdatable.UpdateFixRequest
        | ReleaseStepUpdatable.ApproveFixRequest
        | ReleaseStepUpdatable.RejectFixRequest
        | ReleaseStepUpdatable.MoveFixRequestToPendingApproval
        | ReleaseStepUpdatable.IgnoreFRItem
        | ReleaseStepUpdatable.UnignoreFRItem
    ) ?
        BaseUpdateFeatureWorkItemRequest
    :   BaseUpdateReleaseStepRequest;

interface UpdateReleaseStepRequest<T extends ReleaseStepUpdatable> {
    requestData: UpdateReleaseStepRequestDataTypes<T>;
    resourceIds: UpdateReleaseStepResourceIds<T>;
    id: string;
}

export interface TryUpdateReleaseStepAction<T extends ReleaseStepUpdatable> {
    type: T;
    payload: UpdateReleaseStepRequest<T>;
}

export const tryUpdateReleaseStep: <T extends ReleaseStepUpdatable>(
    type: T,
    resourceIds: UpdateReleaseStepResourceIds<T>,
    requestData: UpdateReleaseStepRequestDataTypes<T>,
    id: string,
) => TryUpdateReleaseStepAction<T> = (type, resourceIds, requestData, id) => {
    return {
        type,
        payload: {
            resourceIds,
            requestData,
            id,
        },
    };
};

export interface FetchReleaseStepSuccessAction {
    type: typeof FETCH_RELEASE_STEP_SUCCESS;
    payload: FetchReleaseStepResponse;
}

export const fetchReleaseStepSuccess: (payload: FetchReleaseStepResponse) => FetchReleaseStepSuccessAction = (
    payload,
) => {
    return {
        type: FETCH_RELEASE_STEP_SUCCESS,
        payload,
    };
};

export interface TryUpdateReleaseStepsAction {
    type: typeof UPDATE_RELEASE_STEPS;
    payload: RunwayModelsResponse['releaseSteps'];
}

export const updateReleaseSteps = (payload: RunwayModelsResponse['releaseSteps']): TryUpdateReleaseStepsAction => {
    return {
        type: UPDATE_RELEASE_STEPS,
        payload: payload,
    };
};
